// Resolve the intake quick-extract's request_type to a selectable dropdown option
// from the tenant's contract_types. Mirrors the backend overlay's match ladder
// (exact first, then GUARDED containment) so the two can't disagree.
//
// ROOT CAUSE (2026-07-20, 3rd failure): the previous inline logic used a plain
// containment filter — since "Services Agreement" is a substring of "Master
// Services Agreement", a correct backend answer of "Master Services Agreement"
// pulled the plain "Services Agreement" row into the candidate set, and
// `.find(category)` then returned whichever came first, silently downgrading the
// type shown in the form. The backend was returning MSA the whole time; the UI
// re-resolved and collided. Fixes: (1) prefer an EXACT name match; (2) a "Master X"
// answer never collapses into a non-master substring type.

export type ContractType = { name?: string | null; category?: string | null };

const norm = (s: string | null | undefined): string =>
  (s || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();

// Returns the canonical dropdown name for the AI type, or "" if none match.
export function resolveIntakeType(
  rawType: string | null | undefined,
  category: string | null | undefined,
  contractTypes: ContractType[]
): string {
  const rawN = norm(rawType);
  if (!rawN) return "";
  const cat = category || "";
  const pickByCategory = (rows: ContractType[]): ContractType | undefined =>
    rows.find((t) => t.category === cat) || rows[0];

  // 1) Exact (normalized) name match — a real "Master Services Agreement" row
  //    always wins over any substring sibling.
  const exact = contractTypes.filter((t) => norm(t.name) === rawN);
  if (exact.length) return pickByCategory(exact)?.name || "";

  // 2) Guarded containment either way.
  const contains = contractTypes.filter((t) => {
    if (/^amendment$/i.test(t.name || "")) return false; // hidden bare row
    const n = norm(t.name);
    if (!n) return false;
    // Substring-collision guard: never collapse "Master X" into non-master "X".
    if (/\bmaster\b/.test(rawN) && !/\bmaster\b/.test(n) && rawN.includes(n)) return false;
    return n.includes(rawN) || rawN.includes(n);
  });
  if (contains.length) return pickByCategory(contains)?.name || "";

  return "";
}
